import React, { useEffect, useState } from 'react';

import { useFormik } from 'formik';
import { injectIntl } from 'react-intl';
import { connect, shallowEqual, useSelector } from 'react-redux';
import { Link, Redirect, useHistory } from 'react-router-dom';
import * as Yup from 'yup';

import { authSlice } from '../redux';
import { confirmSignUp, resendSignUp } from '../redux/api';
import { AUTH_ROUTE } from '../routes';

const initialValues = {
  code: '',
  email: '',
};

function SignUpConfirmation(props) {
  const { intl } = props;
  const history = useHistory();

  const [loading, setLoading] = useState(false);
  const [resend, setResend] = useState(false);
  const [resendActionText, setResendActionText] = useState('Click here to resend it.');
  const [resendText, setResendText] = useState('Haven\'t received the code?');
  const [resending, setResending] = useState(false);
  const [status, setStatus] = useState(false);

  const { cognito, email } = useSelector(({ auth }) => auth, shallowEqual);

  const schema = Yup.object().shape({
    code: Yup.string()
      .min(6, 'Minimum length is 6')
      .required(intl.formatMessage({ id: 'AUTH.VALIDATION.REQUIRED_FIELD' })),

    email: Yup.string()
      .email('Invalid email format')
      .required(intl.formatMessage({ id: 'AUTH.VALIDATION.REQUIRED_FIELD' })),
  });

  const getInputClasses = (fieldname) => {
    if (formik.touched[fieldname] && formik.errors[fieldname]) {
      return 'is-invalid';
    }

    if (formik.touched[fieldname] && !formik.errors[fieldname]) {
      return 'is-valid';
    }

    return '';
  };

  const formik = useFormik({
    initialValues: {
      ...initialValues,
      email: email ?? initialValues.email,
    },
    validationSchema: schema,
    onSubmit: (values, { setSubmitting }) => {
      setLoading(true);
      setStatus();
      setSubmitting(true);

      confirmSignUp(values)
        .then(() => {
          props.confirmSignUp(values);
          history.push(AUTH_ROUTE);
        })
        .catch(error => {
          setStatus(error.message);
          setLoading(false);
          setSubmitting(false);
        });
    },
  });

  useEffect(() => {
    if (email && resend && !resending) {
      setResending(true);

      resendSignUp({ email })
        .then(() => {
          setResendActionText('Click here to resend it again.');
          setResendText('Code sent.');
        })
        .catch(error => {
          setResendActionText('Click here to resend it again.');
          setResendText('Code could not be sent.');
          setStatus(error.message);
        })
        .finally(() => {
          setResend(false);
          setResending(false);
        });
    }
  }, [email, resend, resending]);

  if (cognito?.userConfirmed === true || typeof email !== 'string') {
    return <Redirect to={AUTH_ROUTE} />;
  }

  const resendClick = () => {
    if (email) {
      setResend(true);
    }
  };

  return (
    <div className="login-form login-signin" style={{ display: 'block' }}>
      <div className="text-center mb-10 mb-lg-20">
        <h3 className="font-size-h1">
          Email confirmation
        </h3>
        <p className="text-muted font-weight-bold">
          A confirmation code was sent to your email
        </p>
      </div>

      <form
        id="kt_login_signin_form"
        className="form fv-plugins-bootstrap fv-plugins-framework animated animate__animated animate__backInUp"
        onSubmit={formik.handleSubmit}
      >
        <div className="mb-10 p-3 alert alert-custom alert-light-primary alert-dismissible">
          <div className="alert-text font-weight-bold">
            {resendText}&nbsp;
            {// eslint-disable-next-line
              <a
                className="text-dark-50 text-hover-dark my-3 mr-2"
                onClick={resendClick}
              >{resendActionText}</a>
            }
            {resending && <span className="ml-3 spinner spinner-primary" />}
          </div>
        </div>

        {/* begin: Alert */}
        {status && (
          <div className="mb-10 alert alert-custom alert-light-danger alert-dismissible">
            <div className="alert-text font-weight-bold">{status}</div>
          </div>
        )}
        {/* end: Alert */}

        {/* begin: Email */}
        <div className="form-group fv-plugins-icon-container">
          <input
            placeholder="Email"
            type="email"
            className={`form-control form-control-solid h-auto py-5 px-6 ${getInputClasses(
              'email',
            )}`}
            name="email"
            {...formik.getFieldProps('email')}
          />
          {formik.touched.email && formik.errors.email ? (
            <div className="fv-plugins-message-container">
              <div className="fv-help-block">{formik.errors.email}</div>
            </div>
          ) : null}
        </div>
        {/* end: Email */}

        {/* begin: Code */}
        <div className="form-group fv-plugins-icon-container">
          <input
            placeholder="Code"
            type="code"
            className={`form-control form-control-solid h-auto py-5 px-6 ${getInputClasses(
              'code',
            )}`}
            name="code"
            {...formik.getFieldProps('code')}
          />
          {formik.touched.code && formik.errors.code ? (
            <div className="fv-plugins-message-container">
              <div className="fv-help-block">{formik.errors.code}</div>
            </div>
          ) : null}
        </div>
        {/* end: Code */}

        <div className="form-group d-flex flex-wrap flex-center">
          <button
            type="submit"
            className="btn btn-primary font-weight-bold px-9 py-4 my-3 mx-4"
            disabled={formik.isSubmitting || !formik.isValid}
          >
            <span>Submit</span>
            {loading && <span className="ml-3 spinner spinner-white" />}
          </button>

          <Link to={AUTH_ROUTE}>
            <button
              type="button"
              className="btn btn-light-primary font-weight-bold px-9 py-4 my-3 mx-4"
            >
              Cancel
            </button>
          </Link>
        </div>

      </form>
    </div>
  );
}

export default injectIntl(connect(null, authSlice.actions)(SignUpConfirmation));
